import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '@/services/api';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import AdminLayout from '@/components/layout/AdminLayout';

interface UserDetail {
  id: string;
  name: string;
  email: string;
  phone: string;
  avatar: string;
  role: string;
  roleBadgeColor: string;
  status: 'Active' | 'Inactive';
  legalName: string;
  emailAddress: string;
  phoneNumber: string;
  jobTitle: string;
  department: string;
  joinedDate: string;
}

interface ActivityLogEntry {
  time: string;       // ISO timestamp from backend
  action: string;     // user-friendly label resolved server-side
  status: string;     // 'success' | 'danger' | 'info'
  record: string;     // e.g. 'BOOKING-123'
  details?: string | null;
}

const AdminUserDetailPage = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Profile');
  const [userData, setUserData] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [activityLog, setActivityLog] = useState<ActivityLogEntry[]>([]);

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await apiService.request(`/admin/users/${userId}`, 'GET');
        if (response.success && response.data) {
          setUserData(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch user data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserData();
    }
  }, [userId]);

  // Fetch activity log (audit_log entries performed by this user)
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const res: any = await apiService.request(`/admin/users/${userId}/activity`, 'GET');
        if (cancelled) return;
        setActivityLog(Array.isArray(res?.data) ? res.data : []);
      } catch (err) {
        console.error('Failed to fetch activity log:', err);
        if (!cancelled) setActivityLog([]);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const fmtActivityTime = (iso: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  // Handler: Save user permissions
  const handleSavePermissions = async () => {
    try {
      const response = await apiService.request(`/admin/users/${userId}/update-permissions`, 'PATCH', {
        role: user.role,
        permissionsUpdatedBy: localStorage.getItem('adminId'),
        permissionsUpdatedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Permissions updated successfully');
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Save permissions error:', error);
      alert('Failed to save permissions: ' + error.message);
    }
  };

  // Handler: Deactivate user
  const handleDeactivateUser = async () => {
    if (!confirm('Are you sure you want to deactivate this user? They will no longer be able to access the system.')) return;
    try {
      const response = await apiService.request(`/admin/users/${userId}/deactivate`, 'POST', {
        deactivatedBy: localStorage.getItem('adminId'),
        deactivatedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ User deactivated successfully');
        navigate('/admin/users');
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Deactivate user error:', error);
      alert('Failed to deactivate user: ' + error.message);
    }
  };

  // Show loading or error state
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin">
            <ExternalLink className="text-blue-600" size={32} />
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!userData) {
    return (
      <AdminLayout>
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-2">
              <ExternalLink className="text-red-600" size={24} />
              <h3 className="text-lg font-bold text-red-700">User Not Found</h3>
            </div>
            <p className="text-sm text-red-600 mb-4">
              Unable to fetch user data for ID: {userId}. Please check if the user exists and try again.
            </p>
            <button
              onClick={() => navigate('/admin/users')}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-medium text-sm transition"
            >
              Back to Users
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Use fetched user data
  const user = userData;

  return (
    <AdminLayout>
      <div className="w-full bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-white border-b border-gray-200">
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Settings</span>
              <span className="text-gray-300">›</span>
              <span className="font-semibold text-gray-900">PERMISSIONS</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                4 PERENNIAL YAL
              </div>
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div className="flex-1 overflow-auto">
          <div className="px-6 py-8">
            {/* Back Button and Profile Header */}
            <div className="mb-8">
              <button
                onClick={() => navigate('/admin/users')}
                className="flex items-center gap-2 text-teal-700 hover:text-teal-900 font-medium mb-6"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Admin Users
              </button>

              {/* User Profile Header */}
              <div className="flex items-start justify-between mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-lg bg-gray-300 flex items-center justify-center text-5xl">
                    {user.avatar}
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">{user.name}</h1>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${user.roleBadgeColor}`}>
                        {user.role}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        <span className="text-sm font-medium text-gray-700">{user.status}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setActiveTab('Permissions')}
                  className="bg-teal-700 hover:bg-teal-800 text-white font-semibold px-6 py-2.5 rounded-lg"
                >
                  Edit Access
                </button>
              </div>

              {/* Tabs */}
              <div className="flex gap-8 border-b border-gray-200">
                {['Profile', 'Activity Log', 'Permissions'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`pb-4 font-medium text-sm ${
                      activeTab === tab
                        ? 'text-teal-700 border-b-2 border-teal-700'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            {/* Profile Tab Content */}
            {activeTab === 'Profile' && (
              <div className="space-y-8">
                {/* Personal Info Section */}
                <div>
                  <h2 className="text-xs uppercase font-bold text-gray-600 tracking-wider mb-6">Personal Info</h2>
                  <div className="space-y-6">
                    <div>
                      <p className="text-xs uppercase font-bold text-gray-500 tracking-wider mb-2">Legal Name</p>
                      <p className="text-gray-900 font-medium">{user.legalName}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase font-bold text-gray-500 tracking-wider mb-2">Email Address</p>
                      <p className="text-gray-900 font-medium">{user.emailAddress}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase font-bold text-gray-500 tracking-wider mb-2">Phone Number</p>
                      <p className="text-gray-900 font-medium">{user.phoneNumber}</p>
                    </div>
                  </div>
                </div>

                {/* Employment Section */}
                <div>
                  <h2 className="text-xs uppercase font-bold text-gray-600 tracking-wider mb-6">Employment</h2>
                  <div className="bg-gray-100 rounded-lg p-6 grid grid-cols-2 gap-8">
                    <div>
                      <p className="text-xs uppercase font-bold text-gray-500 tracking-wider mb-2">Job Title</p>
                      <p className="text-gray-900 font-medium">{user.jobTitle}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase font-bold text-gray-500 tracking-wider mb-2">Department</p>
                      <p className="text-gray-900 font-medium">{user.department}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs uppercase font-bold text-gray-500 tracking-wider mb-2">Joined Date</p>
                      <p className="text-gray-900 font-medium">{user.joinedDate}</p>
                    </div>
                  </div>
                </div>

                {/* Account Actions Section */}
                <div>
                  <h2 className="text-xs uppercase font-bold text-gray-600 tracking-wider mb-6">Account Actions</h2>
                  <div className="space-y-3">
                    <button onClick={handleSavePermissions} className="w-full bg-teal-700 hover:bg-teal-800 text-white font-semibold px-6 py-3 rounded-lg flex items-center justify-center gap-2">
                      <span>✎</span>
                      Edit Admin
                    </button>
                    <button onClick={handleDeactivateUser} className="w-full bg-red-50 hover:bg-red-100 text-red-700 font-semibold px-6 py-3 rounded-lg flex items-center justify-center gap-2">
                      <span>⊘</span>
                      Suspend Account
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Activity Log Tab */}
            {activeTab === 'Activity Log' && (
              <div className="grid grid-cols-3 gap-6">
                {/* Left Sidebar - Log Overview and Filters */}
                <div className="space-y-6">
                  {/* Log Overview */}
                  <div className="bg-gray-900 text-white rounded-lg p-6">
                    <h3 className="text-xs uppercase font-bold text-teal-400 tracking-wider mb-6">Log Overview</h3>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-gray-400 mb-2">Successful Actions</p>
                        <p className="text-3xl font-bold text-white">1,204</p>
                        <div className="mt-2 h-1 bg-teal-500 rounded-full"></div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">Security Flags</p>
                        <p className="text-3xl font-bold text-white">0</p>
                      </div>
                    </div>
                  </div>

                  {/* Refine Audit Filters */}
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-xs uppercase font-bold text-gray-600 tracking-wider mb-6">Refine Audit</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="text-xs uppercase font-bold text-gray-600 tracking-wider block mb-2">Action Type</label>
                        <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
                          <option>All Actions</option>
                          <option>Create</option>
                          <option>Update</option>
                          <option>Delete</option>
                          <option>Access</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs uppercase font-bold text-gray-600 tracking-wider block mb-2">Timeframe</label>
                        <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
                          <option>Last 24 Hours</option>
                          <option>Last 7 Days</option>
                          <option>Last 30 Days</option>
                          <option>Last 3 Months</option>
                          <option>All Time</option>
                        </select>
                      </div>
                      <button
                        onClick={async () => {
                          // Re-fetch the activity feed; filter parameters
                          // are read by the backend, so this is sufficient.
                          try {
                            const res: any = await apiService.request(`/admin/users/${userId}/activity?refresh=1`, 'GET');
                            setActivityLog(Array.isArray(res?.data) ? res.data : []);
                          } catch (e: any) {
                            alert('Failed to load: ' + (e?.message || 'Network error'));
                          }
                        }}
                        className="w-full bg-white border border-gray-300 hover:bg-gray-50 text-gray-900 font-medium py-2 rounded-lg text-sm transition-colors"
                      >
                        Apply Filters
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right Content - Activity Log Table */}
                <div className="col-span-2">
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50">
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date & Time</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Action</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Related Record</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">IP Address</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activityLog.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">
                              No activity recorded for this user yet.
                            </td>
                          </tr>
                        ) : activityLog.map((log, index) => (
                          <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="px-6 py-4 text-sm text-gray-600">{fmtActivityTime(log.time)}</td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${
                                  log.status === 'success' ? 'bg-green-500' :
                                  log.status === 'danger' ? 'bg-red-500' :
                                  'bg-blue-500'
                                }`}></span>
                                <span className="text-sm text-gray-900 font-medium">{log.action}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600 font-medium">{log.record}</td>
                            <td className="px-6 py-4 text-sm text-gray-600">—</td>
                            <td className="px-6 py-4">
                              <button
                                title={log.details || ''}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-6 px-1">
                    <p className="text-sm text-gray-600">SHOWING 1-15 OF 1,204 ENTRIES</p>
                    <div className="flex items-center gap-2">
                      <button className="w-8 h-8 rounded flex items-center justify-center hover:bg-gray-100 text-gray-600">‹</button>
                      <button className="w-8 h-8 rounded flex items-center justify-center bg-teal-700 text-white font-semibold">1</button>
                      <button className="w-8 h-8 rounded flex items-center justify-center hover:bg-gray-100 text-gray-600">2</button>
                      <button className="w-8 h-8 rounded flex items-center justify-center hover:bg-gray-100 text-gray-600">3</button>
                      <button className="w-8 h-8 rounded flex items-center justify-center hover:bg-gray-100 text-gray-600">›</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Permissions Tab */}
            {activeTab === 'Permissions' && (
              <div className="space-y-8">
                {/* Permissions Override Header */}
                <div>
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className="text-xs uppercase font-bold text-gray-600 tracking-wider mb-2">Permissions Override</h2>
                      <p className="text-sm text-gray-600">Role-based access levels and individual overrides</p>
                    </div>
                    <div className="bg-teal-50 border border-teal-200 rounded-lg px-4 py-3 flex items-start gap-2">
                      <span className="text-teal-700 font-semibold mt-0.5">🔒</span>
                      <p className="text-sm text-teal-700 font-medium">Permissions for Super Admin are locked to full access.</p>
                    </div>
                  </div>
                </div>

                {/* Permissions Grid */}
                <div className="space-y-8">
                  {/* Operations and Finance Row */}
                  <div className="grid grid-cols-2 gap-8">
                    {/* Operations */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      <div className="flex items-center gap-2 mb-6">
                        <span className="text-lg">📊</span>
                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Operations</h3>
                        <span className="text-xs text-gray-500 ml-auto">MODULE 01</span>
                      </div>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700">Main Dashboard</span>
                          <span className="text-green-600 text-lg">✓</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700">Dispatch Controls</span>
                          <span className="text-green-600 text-lg">✓</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700">Service Logs</span>
                          <span className="text-green-600 text-lg">✓</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700">Rider Onboarding</span>
                          <span className="text-green-600 text-lg">✓</span>
                        </div>
                      </div>
                    </div>

                    {/* Finance */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      <div className="flex items-center gap-2 mb-6">
                        <span className="text-lg">💰</span>
                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Finance</h3>
                      </div>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700">Payouts — Release</span>
                          <span className="text-green-600 text-lg">✓</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700">Revenue Reports</span>
                          <span className="text-green-600 text-lg">✓</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700">Tax Auditing</span>
                          <span className="text-green-600 text-lg">✓</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Marketplace and Compliance Row */}
                  <div className="grid grid-cols-2 gap-8">
                    {/* Marketplace */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      <div className="flex items-center gap-2 mb-6">
                        <span className="text-lg">🏪</span>
                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Marketplace</h3>
                      </div>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700">Store Catalog — Edit</span>
                          <span className="text-green-600 text-lg">✓</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700">Vendor Approvals</span>
                          <span className="text-green-600 text-lg">✓</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700">Promotional Tools</span>
                          <span className="text-green-600 text-lg">✓</span>
                        </div>
                      </div>
                    </div>

                    {/* Compliance */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      <div className="flex items-center gap-2 mb-6">
                        <span className="text-lg">✓</span>
                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Compliance</h3>
                      </div>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700">Identity Verification</span>
                          <span className="text-green-600 text-lg">✓</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700">Risk Assessment</span>
                          <span className="text-yellow-500 text-lg">⊙</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700">Case Management</span>
                          <span className="text-red-600 text-lg">✗</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Legend */}
                <div className="flex items-center gap-4 pt-4">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded flex items-center justify-center bg-green-50 text-green-600 font-bold text-sm">✓</span>
                    <span className="text-xs font-medium text-gray-700 uppercase">Allow</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded flex items-center justify-center bg-yellow-50 text-yellow-600 font-bold text-sm">⊙</span>
                    <span className="text-xs font-medium text-gray-700 uppercase">View Only</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded flex items-center justify-center bg-red-50 text-red-600 font-bold text-sm">✗</span>
                    <span className="text-xs font-medium text-gray-700 uppercase">Denied</span>
                  </div>
                </div>

                {/* Assigned Regions */}
                <div className="bg-teal-800 text-white rounded-lg p-8 mt-8">
                  <h3 className="text-xl font-bold mb-3">Assigned Regions</h3>
                  <p className="text-teal-100 text-sm mb-6">
                    Alex Sterling maintains oversight across primary global nodes. Super Admin privileges bypass regional restrictions but reporting is focused on these areas.
                  </p>
                  <div className="grid grid-cols-3 gap-4 mt-6">
                    <div className="bg-teal-700 rounded-lg p-4 border border-teal-600">
                      <p className="text-xs uppercase font-bold text-teal-200 tracking-wider mb-1">North America</p>
                      <p className="text-lg font-bold text-white">New York HQ</p>
                    </div>
                    <div className="bg-teal-700 rounded-lg p-4 border border-teal-600">
                      <p className="text-xs uppercase font-bold text-teal-200 tracking-wider mb-1">Europe Central</p>
                      <p className="text-lg font-bold text-white">Berlin Office</p>
                    </div>
                    <div className="bg-teal-700 rounded-lg p-4 border border-teal-600">
                      <p className="text-xs uppercase font-bold text-teal-200 tracking-wider mb-1">APAC</p>
                      <p className="text-lg font-bold text-white">Singapore Hub</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminUserDetailPage;
